import { CHESS_AUDIO, inCheck, type State } from '@chessshooter/shared';

export type ChessSound = 'select' | 'move' | 'capture' | 'check' | 'promotion';

/** Observe authoritative positions only: prediction replays must never replay sounds. */
export class ChessSoundEvents {
  private previous: { fen: string; moveTick: number } | null = null;

  reset(state?: State): void {
    this.previous = state ? { fen: state.chess.fen, moveTick: state.chess.lastMoveTick } : null;
  }

  update(state: State): ChessSound | null {
    const previous = this.previous;
    this.reset(state);
    const { fen, lastMove, lastMoveTick } = state.chess;
    if (!previous || state.phase !== 'chess' || !lastMove || lastMoveTick <= previous.moveTick || fen === previous.fen) return null;
    if (inCheck(state)) return 'check';
    if (lastMove.promotion) return 'promotion';
    const count = (f: string) => f.split(' ')[0].replace(/[^a-z]/gi, '').length;
    return count(fen) < count(previous.fen) ? 'capture' : 'move';
  }
}

/** Local Web Audio playback; missing audio or blocked autoplay never blocks a move. */
export class ChessAudio {
  private context: AudioContext | null = null;
  private readonly buffers = new Map<ChessSound, Promise<AudioBuffer | null>>();
  private readonly voices = new Map<string, AudioBufferSourceNode>();
  private readonly requests = new Map<string, object>();
  private enabled: boolean;

  constructor(enabled = true) {
    this.enabled = enabled;
    document.addEventListener('pointerdown', () => this.unlock(), true);
    document.addEventListener('keydown', () => this.unlock(), true);
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.stop(); });
    window.addEventListener('pagehide', () => this.stop());
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.stop();
    else this.unlock();
  }

  unlock(): void {
    if (!this.enabled || document.hidden || typeof AudioContext === 'undefined') return;
    try {
      this.context ??= new AudioContext();
      if (this.context.state === 'suspended') void this.context.resume().catch(() => {});
      for (const name of ['select', 'move', 'capture', 'check', 'promotion'] as const) void this.load(name);
    } catch { /* Audio is optional on browsers without an available output. */ }
  }

  private load(name: ChessSound): Promise<AudioBuffer | null> {
    const cached = this.buffers.get(name);
    if (cached) return cached;
    const ctx = this.context!;
    const loading = (async () => {
      if (name === 'select') {
        const S = CHESS_AUDIO.select;
        const buffer = ctx.createBuffer(1, Math.ceil(S.duration * ctx.sampleRate), ctx.sampleRate);
        const samples = buffer.getChannelData(0);
        for (let i = 0; i < samples.length; i++) {
          const t = i / ctx.sampleRate, progress = t / S.duration;
          const phase = 2 * Math.PI * S.hz * (i + 1) / ctx.sampleRate;
          let value = Math.sin(phase);
          for (const [ratio, gain] of S.partials) value += Math.sin(phase * ratio) * gain * Math.exp(-S.decay * progress * (ratio - 1));
          samples[i] = value * S.gain * Math.min(1, t / S.attack) * Math.exp(-S.decay * progress) * Math.min(1, (S.duration - t) / S.release);
        }
        return buffer;
      }
      const ext = document.createElement('audio').canPlayType('audio/ogg; codecs="vorbis"') ? 'ogg' : 'm4a';
      const response = await fetch(`${import.meta.env.BASE_URL}audio/chess/${name}.${ext}`);
      return response.ok ? await ctx.decodeAudioData(await response.arrayBuffer()) : null;
    })().catch(() => null);
    this.buffers.set(name, loading);
    return loading;
  }

  play(name: ChessSound): void {
    if (!this.enabled || document.hidden || !this.context) return;
    const ctx = this.context;
    const family = name === 'select' ? 'selection' : 'board';
    const request = {}, started = performance.now();
    this.requests.set(family, request);
    void this.load(name).then((buffer) => {
      if (!buffer || !this.enabled || document.hidden || ctx.state !== 'running' ||
        this.requests.get(family) !== request || performance.now() - started > CHESS_AUDIO.maxLateMs) return;
      try {
        this.voices.get(family)?.stop();
        const source = ctx.createBufferSource(), gain = ctx.createGain();
        source.buffer = buffer;
        gain.gain.value = CHESS_AUDIO.volume[name];
        source.connect(gain); gain.connect(ctx.destination);
        this.voices.set(family, source);
        source.onended = () => {
          source.disconnect(); gain.disconnect();
          if (this.voices.get(family) === source) this.voices.delete(family);
        };
        source.start();
      } catch { /* Playback failure must not affect input or the frame loop. */ }
    });
  }

  stop(): void {
    this.requests.clear();
    for (const source of this.voices.values()) {
      try { source.stop(); } catch { /* The source may already have ended. */ }
    }
    this.voices.clear();
  }
}
